import { combine } from "@atlaskit/pragmatic-drag-and-drop/combine";
import { draggable } from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import { dropTargetForElements } from "@atlaskit/pragmatic-drag-and-drop/element/adapter";

import React from "react";

import { DragIndicator } from "@mui/icons-material";
import {
  Box,
  CircularProgress,
  Grid,
  IconButton,
  ListItem,
  ListItemIcon,
  Typography,
  useTheme,
} from "@mui/material";
import { Edit, Navigation, Trash2 } from "react-feather";

import {
  OrderBetween,
  Replacement,
} from "@safetyculture/s12-apis-connect-web/s12/accounts/admin/v1/white_label_pb";

export type ReplacementRow = {
  originalIndex: number;
  id: string;
  searchValue: string;
  replaceValue: string;
  orderKey: string;
};

export const CustomNoRowsOverlay = () => (
  <Box display="flex" alignItems="center" justifyContent="center" height="100%">
    <Typography>No results found.</Typography>
  </Box>
);

export const ColumnHeader = () => {
  const headers = ["Index", "ID", "Search Value", "Replace Value", "Actions"];
  const columnWidths = [1, 3.5, 3, 3, 1];
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  return (
    <ListItem
      sx={{
        backgroundColor: isDark ? theme.palette.background.paper : "#e0e0e0",
        borderBottom: "1px solid #ccc",
        fontWeight: "bold",
      }}
    >
      <Grid container alignItems="center">
        {headers.map((header, index) => (
          <Grid item xs={columnWidths[index]} key={header}>
            <Typography
              variant="body1"
              sx={{ fontSize: "0.9rem", fontWeight: "bold" }}
            >
              {header}
            </Typography>
          </Grid>
        ))}
      </Grid>
    </ListItem>
  );
};

export const RowItem = ({
  row,
  index,
  paginatedRows,
  onEdit,
  onMove,
  onDelete,
  onDrag,
}: {
  row: ReplacementRow;
  index: number;
  paginatedRows: ReplacementRow[];
  onEdit: (row: Replacement, order: OrderBetween) => void;
  onMove: (row: Replacement) => void;
  onDelete: (row: Replacement) => void;
  onDrag?: (source: number, destination: number) => void;
}) => {
  const cellStyles = {
    fontSize: "0.8rem",
    whiteSpace: "normal",
    wordBreak: "break-word",
  };
  const theme = useTheme();
  const [isDragging, setIsDragging] = React.useState(false);
  const [isDropTarget, setIsDropTarget] = React.useState(false);
  const dragHandleRef = React.useRef<HTMLDivElement>(null);
  const rowRef = React.useRef<HTMLLIElement>(null);

  React.useEffect(() => {
    const dragHandle = dragHandleRef.current;
    const rowElement = rowRef.current;
    if (!dragHandle || !rowElement) return;

    return combine(
      draggable({
        element: rowElement,
        dragHandle,
        getInitialData: () => ({ index, rowId: row.id }),
        onDragStart: () => setIsDragging(true),
        onDrop: () => setIsDragging(false),
      }),
      dropTargetForElements({
        element: rowElement,
        getData: () => ({ index }),
        onDragEnter: () => setIsDropTarget(true),
        onDragLeave: () => setIsDropTarget(false),
        onDrop: ({ source }) => {
          setIsDropTarget(false);
          const sourceIndex = source.data.index as number;
          if (sourceIndex !== index && onDrag) {
            onDrag(sourceIndex, index);
          }
        },
      }),
    );
  }, [index, row.id, onDrag]);

  return (
    <ListItem
      ref={rowRef}
      sx={{
        borderBottom: `1px solid ${theme.palette.divider}`,
        backgroundColor: isDragging
          ? theme.palette.action.selected
          : isDropTarget
            ? theme.palette.action.focus
            : theme.palette.mode === "dark"
              ? index % 2 === 0
                ? theme.palette.background.default
                : theme.palette.background.paper
              : index % 2 === 0
                ? "#f5f5f5"
                : "#ffffff",
        "&:hover": {
          backgroundColor: theme.palette.action.hover,
        },
        paddingTop: 0,
        paddingBottom: 0,
        opacity: isDragging ? 0.5 : 1,
        transform: isDragging ? "rotate(2deg)" : "none",
        transition: "opacity 0.2s, transform 0.2s",
      }}
    >
      <Grid container alignItems="center">
        <Grid item xs={0.5}>
          <ListItemIcon ref={dragHandleRef} sx={{ cursor: "grab" }}>
            <DragIndicator />
          </ListItemIcon>
        </Grid>
        <Grid item xs={0.5}>
          <Typography variant="body1" sx={cellStyles}>
            {row.originalIndex + 1}
          </Typography>
        </Grid>
        <Grid item xs={3.5}>
          <Typography variant="body1" sx={cellStyles}>
            {row.id}
          </Typography>
        </Grid>
        <Grid item xs={3} sx={{ paddingRight: "2rem" }}>
          <Typography variant="body1" sx={cellStyles}>
            {row.searchValue}
          </Typography>
        </Grid>
        <Grid item xs={3} sx={{ paddingRight: "2rem" }}>
          <Typography variant="body1" sx={cellStyles}>
            {row.replaceValue}
          </Typography>
        </Grid>
        <Grid item xs={1}>
          <Box display="flex" alignItems="center">
            <IconButton
              color="primary"
              onClick={() =>
                onEdit(
                  new Replacement(row),
                  new OrderBetween({
                    prevKey: paginatedRows[index - 1]?.orderKey || "",
                    nextKey: paginatedRows[index + 1]?.orderKey || "",
                  }),
                )
              }
            >
              <Edit fontSize="small" />
            </IconButton>
            <IconButton
              color="primary"
              onClick={() => onMove(new Replacement(row))}
            >
              <Navigation fontSize="small" />
            </IconButton>
            <IconButton
              color="primary"
              onClick={() => onDelete(new Replacement(row))}
            >
              <Trash2 fontSize="small" />
            </IconButton>
          </Box>
        </Grid>
      </Grid>
    </ListItem>
  );
};

export const LoadingOverlay = () => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        backgroundColor: theme.palette.background.paper,
        zIndex: 10,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        opacity: 0.5,
      }}
    >
      <CircularProgress />
    </Box>
  );
};
